// ============================================
// document-mention-extension.ts — R14/R16 TipTap Mention wiring
// ============================================
//
// Configures `@tiptap/extension-mention` for the document editor:
//
//   - The mention char is `@`.
//   - When the user types `@<query>`, the popup lists matching
//     project tasks AND project members (grouped in the popup by
//     `kind`). The popup also has a dedicated search input that
//     filters the merged list (see `document-mention-list.tsx`).
//   - Selecting an item inserts a Mention node with attrs
//     `{ id, label, kind }`. The kind drives the chip rendering
//     (task chip = status badge, person chip = avatar).
//
// R16 fix: the suggestion `command` now DELETES the `@<query>`
// range before inserting the Mention node — without that range
// delete, the trigger character and the typed query stayed in the
// document next to the new chip (a common TipTap gotcha). The
// `range` is provided by the Mention suggestion plugin via
// `SuggestionProps.range`.
//
// The Mention node is rendered inline as a chip via the React
// NodeView `DocumentMentionChipInner` (see
// `document-mention-node-view.tsx`).

import { ReactRenderer } from '@tiptap/react';
import tippy, { type Instance, type GetReferenceClientRect } from 'tippy.js';
// Pull in the tippy.js CSS so the popup's chrome (background,
// arrow, fade animation) is styled correctly. We override
// background/border/padding in `index.css` to match the dark
// theme; this import is just the tippy defaults.
import 'tippy.js/dist/tippy.css';
import Mention from '@tiptap/extension-mention';
import { ReactNodeViewRenderer } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import type { SuggestionProps } from '@tiptap/suggestion';
import type { ProjectMember, Task } from '@/lib/schemas';
import {
    DocumentMentionList,
    tasksToMentionItems,
    membersToMentionItems,
    filterMentionItems,
    type DocumentMentionItem,
    type DocumentMentionListHandle,
    type DocumentMentionListProps,
} from './document-mention-list';
import { DocumentMentionChipInner } from './document-mention-node-view';

// ============================================
// TYPES
// ============================================

export interface DocumentMentionOptions {
    /** Source of truth for the combined tasks + people list. The
     * popup re-queries on every keystroke (the `query` argument
     * is the partial substring the user typed after the `@`). */
    getItems: (query: string) => DocumentMentionItem[];
    /** When true, render a loading spinner instead of the items list. */
    isLoading?: () => boolean;
    /**
     * R25 (Bug A): project id handed to the picker so it can run its
     * own debounced SERVER-SIDE task search
     * (`/projects/:projectId/tasks?search=…&limit=50&offset=0`).
     * Without it the picker falls back to filtering the parent-provided
     * list locally (pre-fix behaviour).
     */
    projectId?: string;
    /**
     * R16: callback invoked when the user clicks a person chip in
     * the document. The extension stores the callback on the
     * editor's storage so the NodeView can pull it without a
     * context dependency. We expose it separately from the task
     * click so the parent can decide between navigation (task)
     * vs a person menu (R25 Bug B: popover listing the person's
     * assigned tasks).
     *
     * `anchor` is the clicked chip element — the parent uses it to
     * anchor the popover next to the mention the user clicked.
     */
    onTaskMentionClick?: (taskId: string) => void;
    onPersonMentionClick?: (
        personId: string,
        displayName: string,
        anchor?: HTMLElement | null
    ) => void;
}

// ============================================
// SUGGESTION PLUGIN CONFIG
// ============================================

/**
 * R17 (t56) bug fix — "Loading tasks…" infinite.
 *
 * Root cause: the picker was created once via `ReactRenderer` and
 * received `isLoading` as a snapshot. TipTap's mention plugin only
 * re-renders the picker via `component.updateProps()` from inside
 * the suggestion's `onUpdate` callback — which fires when the user
 * types into the trigger. When the React Query state (`isLoading`)
 * flipped from `true` to `false` while the picker was idle (no
 * keystroke), the picker kept the stale `isLoading: true` and
 * showed "Loading tasks…" forever.
 *
 * Fix: store the active `ReactRenderer` instance in a module-scope
 * ref so the parent (`DocumentEditor`) can call `updateProps()`
 * whenever `mentionLoading` changes — not just on keystrokes. The
 * setter is a no-op when the picker isn't mounted (i.e. user
 * hasn't typed `@`).
 */
let activeMentionComponent: ReactRenderer<
    DocumentMentionListHandle,
    DocumentMentionListProps
> | null = null;

/**
 * Push a fresh `isLoading` value to the active picker (if mounted).
 * Called from `DocumentEditor`'s useEffect whenever `mentionLoading`
 * flips — i.e. when the React Query state transitions.
 */
export function pushMentionLoadingState(isLoading: boolean): void {
    if (!activeMentionComponent) return;
    activeMentionComponent.updateProps({
        isLoading,
    });
}

function buildSuggestion(options: DocumentMentionOptions) {
    return {
        items: ({ query }: { query: string }) => options.getItems(query),
        char: '@',

        // Render the popup via tippy.js. The `editor` argument is the
        // TipTap editor instance; we use it to anchor the popover at
        // the caret rect.
        render: () => {
            let component: ReactRenderer<DocumentMentionListHandle, DocumentMentionListProps> | null = null;
            let popup: Instance[] = [];

            // Shared command builder — invoked from both onStart (initial
            // mount) and onUpdate (item list refreshes). We close over
            // `props` so each suggestion session gets its own
            // `editor` + `range`.
            const buildCommand = (props: SuggestionProps<DocumentMentionItem>) =>
                (item: DocumentMentionItem) => {
                    // R16 fix: the suggestion plugin tracks the
                    // `[from, to]` range of the `@<query>` token in
                    // `props.range`. We must DELETE that range
                    // BEFORE inserting the Mention node — otherwise
                    // the literal `@query` text stays in the
                    // document next to the chip. The chain order is
                    //   1. focus (restore caret context)
                    //   2. deleteRange(from = range.from, to = range.to)
                    //   3. insertContent(Mention node)
                    //   4. insertContent(' ') — keeps the chip from
                    //      sticking to the next word, which would
                    //      otherwise make it ambiguous whether the
                    //      mention ends.
                    const range = props.range as { from: number; to: number };
                    props.editor
                        .chain()
                        .focus()
                        .deleteRange({ from: range.from, to: range.to })
                        .insertContent({
                            type: 'mention',
                            attrs: {
                                id: item.id,
                                label: item.label,
                                kind: item.kind,
                            },
                        })
                        .insertContent(' ')
                        .run();
                };

            return {
                onStart: (props: SuggestionProps<DocumentMentionItem>) => {
                    component = new ReactRenderer(DocumentMentionList, {
                        props: {
                            items: options.getItems(props.query),
                            command: buildCommand(props),
                            isLoading: options.isLoading ? options.isLoading() : false,
                            // R25 (Bug A): server-side task search.
                            projectId: options.projectId,
                            // The live `@<query>` typed in the editor —
                            // the picker mirrors it into its search input.
                            query: props.query,
                        } as DocumentMentionListProps,
                        editor: props.editor,
                    });
                    // R17: register the live component so the parent
                    // can push `isLoading` updates via
                    // `pushMentionLoadingState()` whenever React
                    // Query state changes. Without this the picker
                    // stays stuck on the snapshot taken at
                    // `onStart` (which sees `isLoading: true`
                    // before the queries resolve).
                    activeMentionComponent = component;

                    if (!props.clientRect) return;

                    popup = tippy('body', {
                        getReferenceClientRect: props.clientRect as GetReferenceClientRect,
                        appendTo: () => document.body,
                        content: component.element,
                        showOnCreate: true,
                        interactive: true,
                        trigger: 'manual',
                        placement: 'bottom-start',
                        theme: 'document-mention',
                        arrow: false,
                        offset: [0, 8],
                        // We only want ONE tippy instance for the popup
                        // — `multiple: false` + `hideOnClick: false`
                        // so clicking a suggestion doesn't dismiss it
                        // before the mention inserts.
                        hideOnClick: false,
                    });
                },

                onUpdate: (props: SuggestionProps<DocumentMentionItem>) => {
                    component?.updateProps({
                        items: options.getItems(props.query),
                        command: buildCommand(props),
                        isLoading: options.isLoading ? options.isLoading() : false,
                        projectId: options.projectId,
                        query: props.query,
                    } as DocumentMentionListProps);

                    if (!props.clientRect) return;
                    popup[0]?.setProps({
                        getReferenceClientRect: props.clientRect as GetReferenceClientRect,
                    });
                },

                onKeyDown: (props: { event: KeyboardEvent }) => {
                    if (props.event.key === 'Escape') {
                        popup[0]?.hide();
                        return true;
                    }
                    return component?.ref?.onKeyDown({ event: props.event }) ?? false;
                },

                onExit: () => {
                    popup[0]?.destroy();
                    component?.destroy();
                    popup = [];
                    component = null;
                    // R17: clear the module-scope ref so a stale
                    // component isn't held past the picker close.
                    activeMentionComponent = null;
                },
            };
        },
    };
}

// ============================================
// EXTENSION FACTORY
// ============================================

/**
 * Build the Mention extension configured for our document editor.
 * Pass the live `getItems` callback so the popup re-queries on every
 * keystroke.
 */
export function buildDocumentMentionExtension(
    options: DocumentMentionOptions
) {
    return Mention.extend({
        // R19 (t60) — bug 3 fix: the base Mention extension's
        // `addAttributes` only exposes `id`, `label`, and
        // `mentionSuggestionChar`. Our picker (R16) writes a
        // `kind: 'task' | 'person'` attr into every Mention node,
        // but ProseMirror's schema silently DROPS unknown attrs
        // — so `node.attrs.kind` was always `undefined` on the
        // NodeView side, which made the chip default to the
        // task-rendering branch even when the user had selected a
        // person. Result: person chips showed "task" text.
        //
        // We now extend `addAttributes` to declare `kind` (with a
        // backward-compat default of 'task' for R14 nodes that
        // predate the field), `taskId`/`personId` aliases for
        // debugging, and the per-attr HTML serialisation we want.
        addAttributes() {
            return {
                id: {
                    default: null,
                    parseHTML: (element) => element.getAttribute('data-id'),
                    renderHTML: (attributes) => {
                        if (!attributes.id) return {};
                        return { 'data-id': attributes.id };
                    },
                },
                label: {
                    default: null,
                    parseHTML: (element) => element.getAttribute('data-label'),
                    renderHTML: (attributes) => {
                        if (!attributes.label) return {};
                        return { 'data-label': attributes.label };
                    },
                },
                kind: {
                    default: 'task',
                    parseHTML: (element) => {
                        const kind = element.getAttribute('data-mention-kind');
                        return kind === 'person' ? 'person' : 'task';
                    },
                    renderHTML: (attributes) => {
                        return { 'data-mention-kind': attributes.kind ?? 'task' };
                    },
                },
            };
        },
        addStorage() {
            // The NodeView reads the click handlers off the editor's
            // storage so the chip can navigate / notify without
            // coupling the extension to React context. We initialise
            // to `null` and let DocumentEditor wire the real
            // callbacks on mount.
            return {
                documentMentionClick: (options.onTaskMentionClick ?? null) as
                    | ((id: string) => void)
                    | null,
                documentPersonMentionClick:
                    (options.onPersonMentionClick ?? null) as
                        | ((
                              id: string,
                              displayName: string,
                              anchor?: HTMLElement | null
                          ) => void)
                        | null,
            };
        },
        addNodeView() {
            // React NodeView renders the inline mention chip with the
            // current task status. We use NodeViewWrapper so the
            // chip is editable-inside-the-doc but clickable
            // outside-the-text-flow.
            return ReactNodeViewRenderer(DocumentMentionChipInner);
        },
    }).configure({
        // Spreadable attributes the extension applies to every
        // Mention node render. The React NodeView overrides the
        // visual rendering; this still applies to plain-text copy /
        // clipboard / SSR.
        HTMLAttributes: {
            class: 'document-mention-chip',
            'data-testid': 'document-mention-node',
        },
        renderText({ node }) {
            // Plain-text fallback (e.g. when copying the document to
            // markdown). Falls back to "@id" so the link isn't lost.
            return `@${node.attrs.label ?? node.attrs.id}`;
        },
        renderHTML({ node, options }) {
            // Server / clipboard render. We render a simple span — the
            // React NodeView in DocumentEditor renders the rich chip
            // when the doc is mounted in the browser. `options`
            // carries the spreadable HTMLAttributes defined above.
            return [
                'span',
                {
                    ...(options.HTMLAttributes as Record<string, unknown>),
                    'data-mention-id': node.attrs.id,
                    'data-mention-label': node.attrs.label,
                    'data-mention-kind': node.attrs.kind,
                },
                `@${node.attrs.label ?? node.attrs.id}`,
            ];
        },
        suggestion: buildSuggestion(options),
    });
}

// ============================================
// HELPERS
// ============================================

/**
 * Convenience helper — combine tasks + people into a single flat
 * `DocumentMentionItem[]` and build a `getItems` callback that
 * filters by query. Tasks come first (sorted by `useTasks` order),
 * then people (sorted by `useProjectMembers` order). The picker UI
 * groups them by kind automatically.
 *
 * The combined cap is 30 items per kind (60 total) to keep the popup
 * scannable.
 */
export function buildMentionItemsCallback(
    tasks: Task[],
    members: ProjectMember[],
    isLoading?: () => boolean
): DocumentMentionOptions {
    const allTasks = tasksToMentionItems(tasks);
    const allPeople = membersToMentionItems(members);
    const combined = [...allTasks, ...allPeople];

    return {
        getItems: (query: string) => filterMentionItems(combined, query),
        isLoading,
    };
}

// Re-export the lower-level helpers for backward compat — R14 callers
// that only have tasks (e.g. unit tests) can still use
// `buildTaskItemsCallback`.
export function buildTaskItemsCallback(
    tasks: Task[],
    isLoading?: () => boolean
): DocumentMentionOptions {
    const all = tasksToMentionItems(tasks);
    return {
        getItems: (query: string) => filterMentionItems(all, query),
        isLoading,
    };
}
